import {CultureDashboardFilters} from '../../../features/company/culture/dashboard/hooks/useCulturalDashboardPage';
import {CompanyResourceFilter} from '../../../types/CompanyResourceFilter';

export const GetRoutes = {
  companyCulturalDashboard: (filter: CultureDashboardFilters, companyId: string) =>
    `/api/v1/companies/${companyId}/cultural_data?data=${filter}`,
  companyCulturalAnalysisStatus: (companyId: string) => `/api/v1/companies/${companyId}/cultural_analysis_status`,
  companyMatches: (companyId: string) => `/api/v1/companies/${companyId}/matches`,
  companyMatchesSummary: (companyId: string) => `/api/v1/companies/${companyId}/matches/summary`,
  /** `GET /api/v1/companies/:company_id/talent_pool` — response shape should match matches pagination. */
  companyTalentPool: (companyId: string) => `/api/v1/companies/${companyId}/talent_pool`,
  companyTalentPoolSummary: (companyId: string) => `/api/v1/companies/${companyId}/talent_pool/summary`,
  companyTalentPoolProfile: (companyId: string, talentId: string) =>
    `/api/v1/companies/${companyId}/talent_pool_profile/${talentId}`,
  companyMatchDetails: (companyId: string, matchId: string) => `/api/v1/companies/${companyId}/matches/${matchId}`,
  applicantSkills: '/api/v1/applicant_skills',
  applicantProfile: (applicantId: string) => `/api/v1/applicants/${applicantId}/profile`,
  company: (companyId: string) => `/api/v1/companies/${companyId}`,
  companyResource: (companyId: string, resource: CompanyResourceFilter) => `/api/v1/companies/${companyId}/${resource}`,
  applicantOnboarding: (applicantId: string) => `/api/v1/applicants/${applicantId}/onboarding`,
  methodDepartmentCategories: '/api/v1/config/method_department_categories',
  methodTextProfiles: '/api/v1/method_text_profiles',
  professionalAreas: '/api/v1/config/professional_areas',
  targetProfiles: (professionalAreaId: number) => `/api/v1/target_profiles?professional_area_id=${professionalAreaId}`,
  targetProfile: (targetProfileId: string) => `/api/v1/target_profiles/${targetProfileId}`,
  companyDepartmentTargetProfiles: (companyId: string, departmentId: string) =>
    `/api/v1/companies/${companyId}/departments/${departmentId}/target_profiles`,
  companyDepartmentTargetProfile: (companyId: string, departmentId: string, associationId: string) =>
    `/api/v1/companies/${companyId}/departments/${departmentId}/target_profiles/${associationId}`,
  companyAdmins: (companyId: string) => `/api/v1/companies/${companyId}/admins`,
  companySecuritySettings: (companyId: string) => `/api/v1/companies/${companyId}/security_settings`,
  matchRejectionReasons: (rejectionType: string) =>
    `/api/v1/config/match_rejection_reasons?rejection_type=${rejectionType}`,
  matchActivity: (companyId: string, matchId: string) =>
    `/api/v1/companies/${companyId}/matches/${matchId}/match_activity`,
  companyProductConfiguration: (companyId: string) => `/api/v1/companies/${companyId}/modules`,
  screeningApplicants: (companyId: string) => `/api/v1/companies/${companyId}/screening_applicants`,
  screeningApplicantsFilterOptions: (companyId: string) =>
    `/api/v1/companies/${companyId}/screening_applicants/filters`,
  screeningBatchInvitations: (companyId: string) =>
    `/api/v1/companies/${companyId}/screening_applicants/batch_invitations`,
  screeningBatchInvitationDetails: (companyId: string, batchId: string) =>
    `/api/v1/companies/${companyId}/screening_applicants/batch_invitations/${batchId}`,
  screeningApplicantReport: (companyId: string, applicantId: string) =>
    `/api/v1/companies/${companyId}/screening_applicants/${applicantId}/report`,
  userMentions: (companyId: string, matchId: string) =>
    `/api/v1/companies/${companyId}/matches/${matchId}/mentionables`,
  getCampaigns: (companyId: string) => `/api/v1/companies/${companyId}/marketing_campaigns`,
  getFeatureFlags: () => '/api/v1/feature_flags',
  getSurveyStatus: (surveyCode: string) => `/api/v1/company_surveys/${surveyCode}`,
  getCompanySurveys: (companyId: string) => `/api/v1/companies/${companyId}/surveys`,
  getCompanySurveyItems: (questionnaireId: string) =>
    `/api/v1/company_survey_questionnaires/${questionnaireId}/company_survey_items`,
  getApplicantProfile: (applicantId: string) => `/api/v1/applicants/${applicantId}/profile`,
  getMarketingCampaignAnalytics: (campaignId: string) => `/api/v1/marketing_campaigns/${campaignId}/performance`,
  downloadApplicantPdfProfile: (companyId: string, matchId: string) =>
    `/api/v1/companies/${companyId}/matches/${matchId}/download_pdf_profile`,
  coreItemsForCompanyManagers: '/api/v1/method_items?audience=company_managers&core_only=true',
  companyWorkforceProfiles: (companyId: string) => `/api/v1/companies/${companyId}/workforce_profiles`,
  companyWorkforceProfilesCompare: (companyId: string, profileAId: string, profileBId: string) => {
    const params = new URLSearchParams({
      profile_a_id: profileAId,
      profile_b_id: profileBId,
    });
    return `/api/v1/companies/${companyId}/workforce_profiles/compare?${params.toString()}`;
  },
  companyWorkforceProfile: (companyId: string, profileId: string) =>
    `/api/v1/companies/${companyId}/workforce_profiles/${profileId}`,
  downloadWorkforceProfileAnalysisPdf: (companyId: string, profileId: string) =>
    `/api/v1/companies/${companyId}/workforce_profiles/${profileId}/download_analysis_pdf`,
  companyWorkforceProfilesStatus: (companyId: string, resourceIds: string[]) => {
    const params = new URLSearchParams();
    resourceIds.forEach((id) => params.append('resource_ids[]', id));
    const query = params.toString();
    return `/api/v1/companies/${companyId}/workforce_profiles/status${query ? `?${query}` : ''}`;
  },
  companyWorkforceDashboardMetric: (companyId: string, metric: string) =>
    `/api/v1/companies/${companyId}/workforce_profiles/dashboard?metric=${metric}`,
  companyDepartmentHealth: (companyId: string, departmentId: string) =>
    `/api/v1/companies/${companyId}/departments/${departmentId}/health`,
};
